import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BugAttachmentsField } from '@/components/support/BugAttachmentsField'
import { useBugAttachments } from '@/components/support/useBugAttachments'
import { Textarea } from '@/components/ui/textarea'
import { useAuthToken } from '@/hooks/useAuthToken'
import { type BugSeverity, bugReportService } from '@/services/bugReportService'
import { type ClipboardEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type Props = {
  readonly open: boolean
  onOpenChange: (open: boolean) => void
}

const SEVERITIES: BugSeverity[] = ['low', 'medium', 'high', 'critical']

// Canonical Featurebase "Product Area" select option labels. The submitted value
// MUST equal one of these exactly — Featurebase matches select options by label.
// Display labels are localized via `reportBug.area.<key>` (falling back to the label).
const PRODUCT_AREAS: { readonly value: string; readonly key: string }[] = [
  { value: 'Home', key: 'home' },
  { value: 'Settings', key: 'settings' },
  { value: 'Assessment', key: 'assessment' },
  { value: 'Content', key: 'content' },
  { value: 'Live Classes', key: 'liveClasses' },
  { value: 'Writing', key: 'writing' },
  { value: 'Speaking', key: 'speaking' },
  { value: 'Grammar', key: 'grammar' },
  { value: 'Vocabulary', key: 'vocabulary' },
  { value: 'Performance', key: 'performance' },
  { value: 'Admin', key: 'admin' },
]

export default function ReportBugModal({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const { getToken } = useAuthToken()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<BugSeverity>('medium')
  const [area, setArea] = useState('')
  const [steps, setSteps] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const attachments = useBugAttachments()

  const resetAttachments = attachments.reset
  const reset = useCallback(() => {
    setTitle('')
    setDescription('')
    setSeverity('medium')
    setArea('')
    setSteps('')
    resetAttachments()
  }, [resetAttachments])

  // Clear the form whenever the modal closes so reopening starts blank —
  // Cancel and overlay dismiss go through onOpenChange, not handleSubmit.
  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  // Blocked while an upload is in flight: submitting now would file the ticket
  // without the screenshot the reporter just attached.
  const canSubmit =
    title.trim().length >= 3 && description.trim().length > 0 && !isSubmitting && !attachments.isUploading

  // A bug report usually starts with a screenshot in the clipboard, so accept a
  // paste anywhere in the dialog rather than making people save the image first.
  function handlePaste(e: ClipboardEvent<HTMLFormElement>) {
    const files = Array.from(e.clipboardData.files)
    if (files.length > 0) {
      e.preventDefault()
      attachments.addFiles(files)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      const result = await bugReportService.submitBug(
        {
          title: title.trim(),
          description: description.trim(),
          severity,
          area: area.trim() || undefined,
          steps_to_reproduce: steps.trim() || undefined,
          url: window.location.href,
          user_agent: navigator.userAgent,
          attachment_ids: attachments.attachmentIds,
        },
        getToken
      )
      toast.success(t('reportBug.success', { number: result.ticket_number }))
      onOpenChange(false) // closing clears the form via the reset-on-close effect
    } catch (error) {
      console.error('Failed to submit bug report:', error)
      toast.error(t('reportBug.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('reportBug.title')}</DialogTitle>
          <DialogDescription>{t('reportBug.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} onPaste={handlePaste} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='bug-title'>{t('reportBug.fieldTitle')}</Label>
            <Input
              id='bug-title'
              className='bg-white'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('reportBug.fieldTitlePlaceholder')}
              maxLength={200}
              required
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='bug-severity'>{t('reportBug.fieldSeverity')}</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as BugSeverity)}>
                <SelectTrigger id='bug-severity' className='w-full bg-white'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {t(`reportBug.severity.${s}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='bug-area'>{t('reportBug.fieldArea')}</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger id='bug-area' className='w-full bg-white'>
                  <SelectValue placeholder={t('reportBug.fieldAreaPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_AREAS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {t(`reportBug.area.${a.key}`, a.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='bug-description'>{t('reportBug.fieldDescription')}</Label>
            <Textarea
              id='bug-description'
              className='min-h-40 bg-white'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('reportBug.fieldDescriptionPlaceholder')}
              rows={8}
              maxLength={5000}
              required
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='bug-steps'>{t('reportBug.fieldSteps')}</Label>
            <Textarea
              id='bug-steps'
              className='min-h-28 bg-white'
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              placeholder={t('reportBug.fieldStepsPlaceholder')}
              rows={5}
              maxLength={5000}
            />
          </div>

          <BugAttachmentsField attachments={attachments} />

          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              {t('reportBug.cancel')}
            </Button>
            <Button type='submit' disabled={!canSubmit}>
              {isSubmitting ? t('reportBug.submitting') : t('reportBug.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
